import { useLocation } from 'react-router-dom';

import React, { useEffect, useState } from 'react';

import { AppLayout } from '@/components/layout/AppLayout';
import {
  ActivityFeedSkeleton,
  ActivityPageSkeleton,
  BalancesPageSkeleton,
  ExpenseCreateSkeleton,
  ExpenseDetailSkeleton,
  ExpenseEditSkeleton,
  GroupDetailSkeleton,
  GroupFormSkeleton,
  GroupsPageSkeleton,
  MembersPageSkeleton,
  ProfilePageSkeleton,
  ReportsPageSkeleton,
  SettingsPageSkeleton,
  SettlementsPageSkeleton,
} from '@/components/skeleton';

import { useSkeletonDebug } from '@/hooks/useSkeletonDebug';

// Map of path patterns to their corresponding skeleton components
const skeletonMap: Record<string, React.ComponentType> = {
  '/activity': ActivityFeedSkeleton,
  '/groups': GroupsPageSkeleton,
  '/groups/create': GroupFormSkeleton,
  '/groups/[id]': GroupDetailSkeleton,
  '/groups/[id]/edit': GroupFormSkeleton,
  '/groups/[id]/members': MembersPageSkeleton,
  '/groups/[id]/expenses/create': ExpenseCreateSkeleton,
  '/groups/[id]/expenses/[expenseId]': ExpenseDetailSkeleton,
  '/groups/[id]/expenses/[expenseId]/edit': ExpenseEditSkeleton,
  '/groups/[id]/activity': ActivityPageSkeleton,
  '/groups/[id]/balances': BalancesPageSkeleton,
  '/groups/[id]/settlements': SettlementsPageSkeleton,
  '/groups/[id]/reports': ReportsPageSkeleton,
  '/profile': ProfilePageSkeleton,
  '/settings': SettingsPageSkeleton,
};

interface SkeletonDebugLayoutProps {
  children: React.ReactNode;
}

/**
 * Layout wrapper that automatically shows the appropriate skeleton
 * when ?skeleton=true is in the URL
 */
export function SkeletonDebugLayout({ children }: SkeletonDebugLayoutProps) {
  const location = useLocation();
  const pathname = location.pathname;
  const { forceSkeleton, debugDelay } = useSkeletonDebug();
  const [showSkeleton, setShowSkeleton] = useState(false);

  useEffect(() => {
    // Wrap setState in setTimeout to avoid direct state setting in effect
    const initialTimer = setTimeout(() => {
      setShowSkeleton(prev => {
        if (forceSkeleton) {
          if (!prev) return true;
          return prev;
        }
        if (prev) return false;
        return prev;
      });
    }, 0);

    let delayTimer: NodeJS.Timeout | undefined;
    if (forceSkeleton && debugDelay > 0) {
      delayTimer = setTimeout(() => {
        setShowSkeleton(false);
      }, debugDelay);
    }

    return () => {
      clearTimeout(initialTimer);
      if (delayTimer) clearTimeout(delayTimer);
    };
  }, [forceSkeleton, debugDelay, pathname]);

  if (!showSkeleton) {
    return <>{children}</>;
  }

  // Find matching skeleton component for current path
  const SkeletonComponent = findSkeletonForPath(pathname);

  if (SkeletonComponent) {
    // Wrap skeleton in AppLayout to match actual page structure
    // Use createElement to avoid static-components lint error
    return <AppLayout>{React.createElement(SkeletonComponent)}</AppLayout>;
  }

  // Fallback to showing children if no skeleton found
  return <>{children}</>;
}

/**
 * Find the appropriate skeleton component for a given path
 */
function findSkeletonForPath(pathname: string): React.ComponentType | null {
  // Direct match
  if (skeletonMap[pathname]) {
    return skeletonMap[pathname];
  }

  // Pattern matching for dynamic routes
  for (const [pattern, component] of Object.entries(skeletonMap)) {
    const regex = createPathRegex(pattern);
    if (regex.test(pathname)) {
      return component;
    }
  }

  return null;
}

/**
 * Convert a Next.js-style path pattern to a regex
 * e.g., "/groups/[id]" becomes /^\/groups\/[^\/]+$/
 */
function createPathRegex(pattern: string): RegExp {
  const regexPattern = pattern
    .replace(/\[([^\]]+)\]/g, '[^/]+') // Replace [param] with [^/]+
    .replace(/\//g, '\\/'); // Escape forward slashes
  return new RegExp(`^${regexPattern}$`);
}
